import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { IMAGES, LOGO_URL, BRAND } from "../brand";
import { Building2, CalendarCheck2, AlertOctagon } from "lucide-react";
import api, { API } from "../api";
import axios from "axios";

export default function PartnerPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/portal/${token}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Portal inválido"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center"><p className="text-sm text-[color:var(--ink-500)]">Carregando portal...</p></div>;
  }
  if (err) {
    return (
      <div className="min-h-screen grid place-items-center px-6">
        <div className="card-arya p-10 max-w-md text-center">
          <AlertOctagon className="mx-auto text-[color:var(--danger)] mb-3"/>
          <h1 className="font-serif text-2xl">Acesso indisponível</h1>
          <p className="text-sm text-[color:var(--ink-500)] mt-2">{err}</p>
          <p className="text-xs text-[color:var(--ink-500)] mt-6">Solicite um novo link ao Arya Studio.</p>
        </div>
      </div>
    );
  }

  const upcoming = (data.appointments || []).filter(a => new Date(a.start_at) >= new Date() && a.status !== "cancelled");
  const past = (data.appointments || []).filter(a => new Date(a.start_at) < new Date() || a.status === "cancelled");

  return (
    <div className="min-h-screen">
      <header className="border-b border-[var(--line)] bg-[rgba(251,246,236,0.85)] backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="w-11 h-11 object-contain"/>
            <div>
              <div className="font-serif text-lg tracking-widest">ARYA STUDIO</div>
              <div className="font-display text-[10px] tracking-[0.3em] text-[color:var(--sun-600)]">PORTAL DE PARCEIROS</div>
            </div>
          </div>
          <div className="text-right">
            <div className="tag-sun inline-flex items-center gap-1"><Building2 size={12}/> {data.partner.segment}</div>
            <p className="font-serif text-xl mt-1">{data.partner.name}</p>
          </div>
        </div>
      </header>

      {/* Hero brand strip */}
      <section className="relative overflow-hidden border-b border-[var(--line)]">
        <div className="absolute inset-0">
          <img src={IMAGES.interior} alt="" className="w-full h-full object-cover opacity-40"/>
          <div className="absolute inset-0 bg-gradient-to-r from-[#FBF6EC] via-[rgba(251,246,236,0.7)] to-transparent"/>
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center gap-6">
          <img src={LOGO_URL} alt="" className="w-24 h-24 object-contain drop-shadow"/>
          <div>
            <p className="font-script text-2xl text-[color:var(--sun-600)]">{BRAND.tagline}</p>
            <h1 className="font-serif text-3xl sm:text-4xl mt-1">Bem-vindos, {data.partner.name}</h1>
            <p className="text-sm text-[color:var(--ink-500)] mt-2 max-w-2xl">
              Este portal é <b>restrito e somente leitura</b>. Acompanhe os clientes vinculados à sua parceria — todos os dados sensíveis permanecem preservados.
            </p>
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-6 py-10">

        <Section title="Próximos agendamentos" icon={<CalendarCheck2 size={16}/>} items={upcoming} empty="Nenhum cliente agendado para os próximos dias."/>
        <div className="mt-10"/>
        <Section title="Histórico" icon={<CalendarCheck2 size={16}/>} items={past} empty="Sem histórico ainda."/>

        <p className="text-xs text-[color:var(--ink-500)] mt-16 text-center">
          Portal restrito · {BRAND.tagline} · © {new Date().getFullYear()} Arya Studio
        </p>
      </main>
    </div>
  );
}

function Section({ title, icon, items, empty }) {
  return (
    <div className="card-arya overflow-hidden">
      <div className="px-6 py-4 border-b border-[var(--line)] flex items-center gap-2">
        {icon} <h2 className="font-serif text-xl">{title}</h2>
        <span className="ml-auto tag-sun">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="p-8 text-center text-sm text-[color:var(--ink-500)]">{empty}</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-[var(--cream-100)] text-left text-xs tracking-widest font-display">
            <tr><th className="p-4">CLIENTE</th><th className="p-4">SERVIÇO</th><th className="p-4">DATA</th><th className="p-4">STATUS</th></tr>
          </thead>
          <tbody className="divide-y divide-[var(--line)]">
            {items.map(a => (
              <tr key={a.appointment_id}>
                <td className="p-4 font-display font-semibold">{a.client_display}</td>
                <td className="p-4">{a.service_title}</td>
                <td className="p-4">{new Date(a.start_at).toLocaleString("pt-BR", { dateStyle:"short", timeStyle:"short" })}</td>
                <td className="p-4"><span className="tag-sun">{a.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
